import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorEl, anchorMissing } from './tasks-help.shared';

/**
 * ?-Hilfe des Reiters „Lage“ (`/tasks/lage`, Paket F14 der Designgrundlage, 09.10.2026).
 *
 * Eine Station je Karte, in der Reihenfolge der Seite (Kap. 3: jede Karte beantwortet genau
 * eine Frage), plus Begrüßung und Export — elf Stationen, die Obergrenze.
 *
 * Was die Schrittliste erklärt:
 *  - **Karten erscheinen erst nach dem ersten Laden** (davor Skelett). Die Begrüßung hängt an
 *    der Seitenwurzel, die immer steht; Kacheln, „Was zuerst“ und „Wo stockt es?“ sind danach
 *    immer da und deshalb nicht optional.
 *  - **Was an einer Freigabe hängt, fehlt ganz** (E-05): „Wer trägt was?“ nur mit Team-Freigabe
 *    (L2), „Halten wir Pflichten ein?“ und das Export-Menü nur mit Nachweis-Freigabe (L5). Diese
 *    Stationen prüfen in `skipIf` nur die Existenz.
 *  - **Karten, die sich selbst ausblenden, behalten ihren Host** (Stock-Karte bei `!visible()`,
 *    Durchlaufzeit bei 403): dort zählt die Höhe, sonst hinge die Station 8 s im
 *    Sichtbarkeits-Guard.
 *
 * Seiten-Tour, also KEIN `dialogContext`. `readyAnchor` startet sie über das ? an Ort und
 * Stelle — ohne ihn navigierte der Coordinator erneut auf `/tasks/lage` und verlöre die
 * Filter aus der Adresse.
 */
const page = { placement: 'bottom' as const, nextCondition: 'manual' as const, showNextButton: true };

/** Host fehlt ODER steht ohne Inhalt im DOM (Karte hat sich selbst ausgeblendet). */
function emptyHost(anchor: AnchorId): boolean {
  if (typeof document === 'undefined') return false;
  const el = anchorEl(anchor);
  return !el || el.getBoundingClientRect().height < 8;
}

export const TASK_LAGE_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskLageHelp,
  version: 1,
  title: 'TASK_LAGE_HELP_TOUR_TITLE',
  description: 'TASK_LAGE_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  launchContext: { route: '/tasks/lage', readyAnchor: AnchorId.TasksLagePage, mode: 'auto' },
  triggerContext: { route: '/tasks/lage' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 190,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_LAGE_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'lg-welcome',
          anchor: AnchorId.TasksLagePage,
          spotlightAnchor: AnchorId.TasksLagePage,
          title: 'TUTORIAL.TASK_LAGE.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_LAGE.WELCOME.CONTENT',
          ...page,
          align: 'start',
          showPrevButton: false,
        },
        {
          id: 'lg-filters',
          anchor: AnchorId.TasksLageFilters,
          spotlightAnchor: AnchorId.TasksLageFilters,
          title: 'TUTORIAL.TASK_LAGE.FILTERS.TITLE',
          content: 'TUTORIAL.TASK_LAGE.FILTERS.CONTENT',
          ...page,
        },
        {
          id: 'lg-kpis',
          anchor: AnchorId.TasksLageKpis,
          spotlightAnchor: AnchorId.TasksLageKpis,
          title: 'TUTORIAL.TASK_LAGE.KPIS.TITLE',
          content: 'TUTORIAL.TASK_LAGE.KPIS.CONTENT',
          ...page,
        },
        {
          id: 'lg-first',
          anchor: AnchorId.TasksLageFirst,
          spotlightAnchor: AnchorId.TasksLageFirst,
          title: 'TUTORIAL.TASK_LAGE.FIRST.TITLE',
          content: 'TUTORIAL.TASK_LAGE.FIRST.CONTENT',
          ...page,
        },
        {
          // Umschalter „Bereiche“ | „Personen“ und Marke „Engpass“ liegen IN der Karte und haben
          // keinen eigenen Anker — der Text nennt sie, das Spotlight umfasst die ganze Karte.
          id: 'lg-stock',
          anchor: AnchorId.TasksLageStock,
          spotlightAnchor: AnchorId.TasksLageStock,
          title: 'TUTORIAL.TASK_LAGE.STOCK.TITLE',
          content: 'TUTORIAL.TASK_LAGE.STOCK.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => emptyHost(AnchorId.TasksLageStock),
        },
        {
          id: 'lg-flow',
          anchor: AnchorId.TasksLageFlow,
          spotlightAnchor: AnchorId.TasksLageFlow,
          title: 'TUTORIAL.TASK_LAGE.FLOW.TITLE',
          content: 'TUTORIAL.TASK_LAGE.FLOW.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => emptyHost(AnchorId.TasksLageFlow),
        },
        {
          id: 'lg-leadtime',
          anchor: AnchorId.TasksLageLeadtime,
          spotlightAnchor: AnchorId.TasksLageLeadtime,
          title: 'TUTORIAL.TASK_LAGE.LEADTIME.TITLE',
          content: 'TUTORIAL.TASK_LAGE.LEADTIME.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => emptyHost(AnchorId.TasksLageLeadtime),
        },
        {
          // Nur mit Team-Freigabe (L2) im DOM.
          id: 'lg-people',
          anchor: AnchorId.TasksLagePeople,
          spotlightAnchor: AnchorId.TasksLagePeople,
          title: 'TUTORIAL.TASK_LAGE.PEOPLE.TITLE',
          content: 'TUTORIAL.TASK_LAGE.PEOPLE.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => emptyHost(AnchorId.TasksLagePeople),
        },
        {
          // Nur mit Nachweis-Freigabe (L5) im Raster.
          id: 'lg-duties',
          anchor: AnchorId.TasksLageDuties,
          spotlightAnchor: AnchorId.TasksLageDuties,
          title: 'TUTORIAL.TASK_LAGE.DUTIES.TITLE',
          content: 'TUTORIAL.TASK_LAGE.DUTIES.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksLageDuties),
        },
        {
          // Eingeklappt: die Station zeigt die Kopfzeile und klappt NICHT auf — Aufklappen
          // startet zwei Abrufe und schiebt die Seite unter dem Popover weg.
          id: 'lg-more',
          anchor: AnchorId.TasksLageMore,
          spotlightAnchor: AnchorId.TasksLageMore,
          title: 'TUTORIAL.TASK_LAGE.MORE.TITLE',
          content: 'TUTORIAL.TASK_LAGE.MORE.CONTENT',
          ...page,
          placement: 'top',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksLageMore),
        },
        {
          // Export-Menü nur mit Nachweis-Freigabe (`nachweiseAllowed`).
          id: 'lg-export',
          anchor: AnchorId.TasksLageExport,
          spotlightAnchor: AnchorId.TasksLageExport,
          title: 'TUTORIAL.TASK_LAGE.EXPORT.TITLE',
          content: 'TUTORIAL.TASK_LAGE.EXPORT.CONTENT',
          ...page,
          align: 'end',
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TasksLageExport),
        },
      ],
    },
  ],
};
